import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, X, ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../lib/api';

interface PaymentRequest {
  _id: string;
  userId?: { profile?: { name?: string }; email?: string };
  itemType?: 'subscription' | 'book';
  requestedPlan?: string;
  bookId?: { title?: { en?: string }; coverImageUrl?: string; price?: number; currency?: string };
  paymentProofNote?: string;
  createdAt: string;
}

function ItemBadge({ req }: { req: PaymentRequest }) {
  if (req.itemType === 'book') {
    return (
      <div className="flex items-center gap-2">
        <span className="badge bg-blue-50 text-blue-700 flex items-center gap-1">
          <BookOpen size={11} /> Book
        </span>
        <span className="text-xs text-gray-600 truncate max-w-[10rem]">
          {req.bookId?.title?.en || 'Unknown book'}
          {req.bookId?.price ? ` · ${req.bookId.currency || 'NPR'} ${req.bookId.price}` : ''}
        </span>
      </div>
    );
  }
  return <span className="badge bg-purple-50 text-purple-700">{req.requestedPlan}</span>;
}

export default function PaymentsPage() {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['payments', page],
    queryFn: async () => {
      const res = await api.get('/payments/pending', { params: { page, limit: 20 } });
      return res.data;
    },
  });

  const approveMutation = useMutation({
    mutationFn: async ({ id, durationMonths }: { id: string; durationMonths?: number }) => {
      await api.post(`/payments/${id}/approve`, durationMonths ? { durationMonths } : {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      toast.success('Payment approved');
    },
    onError: () => toast.error('Failed to approve'),
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await api.post(`/payments/${id}/reject`, { reason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      toast.success('Payment rejected');
    },
    onError: () => toast.error('Failed to reject'),
  });

  const requests = data?.data ?? [];
  const pagination = data?.pagination ?? { total: 0, page: 1, totalPages: 1 };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Payment Requests</h1>
        <p className="text-gray-500 mt-1">{pagination.total} pending requests</p>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-500">User</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Item</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Note</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Date</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-400">
                    No pending requests
                  </td>
                </tr>
              ) : (
                requests.map((req: PaymentRequest) => (
                  <tr key={req._id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-gray-900">
                          {req.userId?.profile?.name || 'Unknown'}
                        </p>
                        <p className="text-xs text-gray-500">{req.userId?.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <ItemBadge req={req} />
                    </td>
                    <td className="py-3 px-4 text-gray-600 max-w-xs truncate">
                      {req.paymentProofNote || '-'}
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {new Date(req.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() =>
                            approveMutation.mutate(
                              req.itemType === 'book'
                                ? { id: req._id }
                                : { id: req._id, durationMonths: 1 },
                            )
                          }
                          className="btn-primary text-xs flex items-center gap-1 !px-3 !py-1"
                        >
                          <Check size={12} /> Approve
                        </button>
                        <button
                          onClick={() =>
                            rejectMutation.mutate({ id: req._id, reason: 'Invalid proof' })
                          }
                          className="btn-danger text-xs flex items-center gap-1 !px-3 !py-1"
                        >
                          <X size={12} /> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="btn-secondary text-xs flex items-center gap-1"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= pagination.totalPages}
              className="btn-secondary text-xs flex items-center gap-1"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
